<template>
  <div class="exit--question feedback">
    <v-btn
      height="56"
      class="feedback-rejoin"
      variant="outlined"
      size="large"
      color="primary"
      prepend-icon="msf:call"
      @click="reload"
    >
      {{ $t('endCall.feedback.joinAgain') }}
    </v-btn>
    <div v-if="(!isShortCall && user.isAssistant) || user.isClient" class="feedback-wrapper">
      <v-card v-if="submitted" class="feedback-card" variant="outlined">
        <div class="feedback-thanks">
          <v-icon color="primary" size="48">check</v-icon>
          <p class="feedback-title">{{ $t('endCall.feedback.thanks') }}</p>
          <p class="feedback-subThanks">
            {{ $t('endCall.feedback.subThanks') }}
          </p>
        </div>
      </v-card>
      <v-card v-else class="feedback-card" variant="outlined">
        <div class="feedback-header">
          <p class="feedback-title">
            {{ user.isAssistant ? $t('endCall.feedback.issueResolved') : $t('endCall.feedback.sessionSatisfaction') }}
          </p>
          <p class="feedback-subtitle" v-if="user.isAssistant">
            {{ exit.solved ? $t('endCall.feedback.subIssueResolved') : $t('endCall.feedback.subSessionSatisfaction') }}
          </p>
          <v-row class="feedback-choices" dense>
            <v-col class="feedback-choices__col" cols="6">
              <v-btn
                class="feedback-choice feedback-choice--resolved"
                block
                flat
                stacked
                rounded="sm"
                height="75"
                :prepend-icon="user.isAssistant ? 'msf:check_circle' : 'msf:thumb_up'"
                data-test-id="radio-feedback-positive"
                @click="selectResult('yes')"
              >
                {{ user.isAssistant ? $t('endCall.feedback.resolved') : $t('endCall.feedback.satisfied') }}
              </v-btn>
            </v-col>
            <v-col class="feedback-choices__col" cols="6">
              <v-btn
                class="feedback-choice feedback-choice--unresolved"
                block
                flat
                stacked
                rounded="sm"
                height="75"
                :prepend-icon="user.isAssistant ? 'msf:do_not_disturb_on' : 'msf:thumb_down'"
                :class="{'feedback-choice--active': exit.solved === 'no' && user.isAssistant}"
                data-test-id="radio-feedback-negative"
                @click="selectResult('no')"
              >
                {{ user.isAssistant ? $t('endCall.feedback.notResolved') : $t('endCall.feedback.notSatisfied') }}
              </v-btn>
            </v-col>
          </v-row>
        </div>

        <template v-if="showIssues">
          <v-divider class="feedback-divider" />
          <div class="feedback-issues">
            <v-radio-group class="feedback-radio-group" v-model="issue" hide-details density="compact">
              <v-radio
                v-for="item in issues"
                :key="item.value"
                class="feedback-radio"
                :value="item.value"
                color="primary"
                :data-test-id="`radio-issue-${item.value}`"
              >
                <template #label><span class="feedback-radio__label" v-html="item.label"></span></template>
              </v-radio>
              <v-radio
                class="feedback-radio feedback-radio--other"
                value="other"
                color="primary"
                data-test-id="radio-issue-other"
              >
                <template #label>
                  <span
                    class="feedback-radio__label"
                    v-html="bracketsToSmall($t('endCall.feedback.step1.anotherIssue'))"
                  ></span>
                </template>
              </v-radio>
            </v-radio-group>
            <v-textarea
              v-if="issue === 'other'"
              ref="other"
              class="feedback-textarea"
              color="primary"
              :autofocus="issue === 'other'"
              v-model="otherIssue"
              variant="outlined"
              rows="4"
              no-resize
              hide-details
              autocomplete="off"
              :placeholder="$t('endCall.feedback.placeholder')"
              data-test-id="feedback-comment"
            />
          </div>
        </template>

        <div v-if="showIssues" class="feedback-actions">
          <v-btn
            class="feedback-save"
            block
            flat
            color="primary"
            size="large"
            data-test-id="button-submit-feedback"
            :disabled="!issue || submitting"
            @click="submit"
          >
            {{ $t('endCall.feedback.save') }}
          </v-btn>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script setup>
import {computed, ref} from 'vue'
import {useExitStore, useSessionStore, useUserStore} from '@/stores'
import {useRepository} from '@/composables'
import {useTranslation} from 'i18next-vue'
import {mediator} from '@communication'
import {useAsyncState, useEventBus} from '@vueuse/core'

const user = useUserStore()
const exit = useExitStore()
const session = useSessionStore()

const {sendStats, sendWsEvent} = useRepository()

const {t} = useTranslation()
const issue = ref('')
const otherIssue = ref('')
const showIssues = computed(() => exit.solved === 'no' && issues.value.length && user.isAssistant)
const callDuration = computed(() => Math.round(session.duration))
const isShortCall = computed(() => callDuration.value <= 0)

const emit = defineEmits(['next'])

const bracketsToSmall = value => {
  const groups = value.match(/([^(]+)(\(([^)]+)\))?/)
  const small = groups[3] ? ` <small>${groups[3]}</small>` : ''
  return groups[1].trim() + small
}

const issues = computed(() => {
  const list = t('endCall.feedback.step1.issues', {returnObjects: true})
  if (!list || typeof list !== 'object') return []
  return Object.values(list).map((label, index) => ({
    label: bracketsToSmall(label),
    value: (index + 1).toString(),
  }))
})

const reload = () => {
  window.location.reload()
}

const {isLoading: submitting, isReady: submitted, execute} = useAsyncState(submitFeedback, null, {immediate: false})

async function submit() {
  if (submitting.value) return
  await execute()
}

async function selectResult(value) {
  exit.solved = value
  if (!showIssues.value) await submit()
}

async function submitFeedback() {
  const {isSolved} = exit
  const {id: partyId} = mediator.session.user
  const session = useSessionStore()
  const report = {
    partyId,
    account: session.info.account,
    team: session.info.team ?? '',
    license: session.info.license,
  }
  if (user.isAssistant) {
    report.assistantFeedback = isSolved
    report.assistantReason1 = !isSolved && issue.value === '1'
    report.assistantReason2 = !isSolved && issue.value === '2'
    report.assistantReason3 = !isSolved && issue.value === '3'
    report.assistantReason4 = !isSolved && issue.value === '4'
    report.assistantReason5 = !isSolved && issue.value === 'other' ? otherIssue.value : ''
    sendWsEvent({
      type: 'feedback:report',
      feedbackSolved: isSolved,
      feedbackOther: otherIssue.value,
      feedbackIssue1: issue.value === issues.value[0]?.value,
      feedbackIssue2: issue.value === issues.value[1]?.value,
      feedbackIssue3: issue.value === issues.value[2]?.value,
      feedbackIssue4: issue.value === issues.value[3]?.value,
      account: session.info.account,
      team: session.info.team ?? '',
      license: session.info.license,
    })
  } else if (user.isClient) {
    report.clientFeedback = isSolved
    sendWsEvent({
      type: 'feedback:report',
      feedbackSolved: isSolved,
      account: session.info.account,
      team: session.info.team ?? '',
      license: session.info.license,
    })
  }
  await sendStats(report)
  if (!isSolved) {
    useEventBus('log:dumpTrigger').emit({reason: 'negative-feedback'})
  }
}
</script>

<style lang="scss" scoped>
.feedback-thanks {
  padding: 30px 24px;
  text-align: center;

  &__icon {
    margin-bottom: 12px;
  }

  &__title {
    font-size: 20px;
    font-weight: 400;
  }
}

.feedback-rejoin {
  width: 100%;
  margin-bottom: 32px;
}

.feedback-card {
  background: $background-neutral-0;
  border-radius: $radius-base;
  border: 1px solid $border-divider-strong;
}

.feedback-header {
  padding: 30px;
  text-align: center;
}

.feedback-title {
  margin-top: 0;
  font-size: 20px;
}

.feedback-subThanks {
  font-size: 14px;
  color: $font-black-1;
}

.feedback-subtitle {
  margin: 10px 0 24px 0;
  font-size: 14px;
  opacity: 0.6;
}

.feedback-choice {
  background: $background-neutral-20;
  color: $icon-black-2;
  font-size: 15px;
  font-weight: 400;

  :deep(.v-btn__prepend) {
    margin: 0 0 4px;

    .v-icon {
      font-size: 24px;
    }
  }

  &--resolved {
    border: 1px solid transparent;
    transition:
      background-color 0.15s,
      color 0.15s,
      border-color 0.15s;

    :deep(.v-btn__overlay) {
      display: none;
    }

    &:hover,
    &:active {
      background: linear-gradient($alert-success-subtle, $alert-success-subtle), $state-hovered-lighter;
      color: $alert-success-strong;
    }
  }

  &--unresolved {
    &:hover {
      background: linear-gradient($alert-attention-subtle, $alert-attention-subtle), $state-hovered-lighter;
      color: $alert-attention-strong;
    }
  }

  &--unresolved.feedback-choice--active {
    background: $alert-attention-subtle;
    color: $alert-attention-strong;
  }
}

.feedback-issues {
  padding: 24px 40px;
  background: $background-neutral-6;
}

.feedback-radio {
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }

  &__label {
    color: $font-black-1;
    font-size: 14px;
    margin-left: 8px;

    :deep(small) {
      font-style: italic;
      padding: 0 4px;
      color: $font-gray-2;
      font-size: 12px;
    }
  }
}

.feedback-textarea {
  margin-top: 12px;
}

.feedback-actions {
  padding: 0 40px 24px 40px;
  background: $background-neutral-6;
}
</style>
